/**
 * NEW FILE (additive — no pre-existing file was modified in place to create this).
 * Used via `appMainScrollAdaptive` on `mat-sidenav-content` in homepage.component.html.
 *
 * The main shell often scrolls inside `.mat-mdc-tab-body-content`
 * (see homepage.component.scss), not only on the host — this directive
 * applies the same overflow logic to those nested surfaces.
 */
import {
  AfterViewInit,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
} from '@angular/core';

const TAB_BODY_SELECTORS =
  '.mat-mdc-tab-body-content, .mat-tab-body-content';

@Directive({
  selector: '[appMainScrollAdaptive]',
})
export class MainScrollAdaptiveDirective implements AfterViewInit, OnDestroy {
  private resizeObserver?: ResizeObserver;
  private bodyClassObserver?: MutationObserver;
  private htmlClassObserver?: MutationObserver;
  private hostMutationObserver?: MutationObserver;
  private readonly observed = new Set<Element>();

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    const host = this.el.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.applyOverflow(host));
      this.observeElement(host);

      this.bodyClassObserver = new MutationObserver(() =>
        this.applyOverflow(host)
      );
      this.bodyClassObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });

      this.htmlClassObserver = new MutationObserver(() =>
        this.applyOverflow(host)
      );
      this.htmlClassObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });

      this.hostMutationObserver = new MutationObserver(() => {
        this.refreshObservedTargets(host);
        this.applyOverflow(host);
      });
      this.hostMutationObserver.observe(host, { subtree: true, childList: true });

      this.refreshObservedTargets(host);
      requestAnimationFrame(() => this.applyOverflow(host));
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.bodyClassObserver?.disconnect();
    this.htmlClassObserver?.disconnect();
    this.hostMutationObserver?.disconnect();
    this.observed.clear();
  }

  private refreshObservedTargets(host: HTMLElement): void {
    const ro = this.resizeObserver;
    if (!ro) {
      return;
    }
    host.querySelectorAll<HTMLElement>(TAB_BODY_SELECTORS).forEach((el) => {
      if (!this.observed.has(el)) {
        this.observed.add(el);
        ro.observe(el);
      }
    });
  }

  private observeElement(el: Element): void {
    if (!this.resizeObserver || this.observed.has(el)) {
      return;
    }
    this.observed.add(el);
    this.resizeObserver.observe(el);
  }

  private applyOverflow(host: HTMLElement): void {
    this.refreshObservedTargets(host);

    if (this.isScrollLocked()) {
      this.clearInlineOverflow(host);
      return;
    }

    const pad = 2;
    const surfaces: HTMLElement[] = [host];
    host.querySelectorAll<HTMLElement>(TAB_BODY_SELECTORS).forEach((el) => {
      if (el.getClientRects().length > 0) {
        surfaces.push(el);
      }
    });

    for (const el of surfaces) {
      if (el.scrollHeight <= el.clientHeight + pad) {
        el.style.setProperty('overflow-y', 'hidden');
      } else {
        el.style.setProperty('overflow-y', 'auto');
      }
    }
  }

  private clearInlineOverflow(host: HTMLElement): void {
    host.style.removeProperty('overflow-y');
    host
      .querySelectorAll<HTMLElement>(TAB_BODY_SELECTORS)
      .forEach((el) => el.style.removeProperty('overflow-y'));
  }

  private isScrollLocked(): boolean {
    const b = document.body;
    return (
      b.classList.contains('modal-open') ||
      b.classList.contains('app-main-scroll-locked') ||
      document.documentElement.classList.contains('cdk-global-scrollblock')
    );
  }
}
